import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { format, startOfMonth } from 'date-fns';

const chamarRpc = (nome: string, parametros: Record<string, unknown>) =>
  supabase.rpc(nome as never, parametros as never) as unknown as PromiseLike<{
    data: unknown;
    error: { message: string } | null;
  }>;

export type TipoBko = 'CONTRATO' | 'TRAMITACAO' | 'CREDITO' | 'PORTABILIDADE' | 'ANALISTA' | 'CONSULTOR' | 'SUPERVISOR_COMERCIAL' | 'SUPERVISOR_QUALIDADE' | 'GERENTE';
export type FaixaNotaBko = 'verde_forte' | 'verde' | 'amarelo' | 'laranja' | 'vermelho';

export interface PilarNotaBko {
  pilar: string;
  rotulo?: string;
  peso: number;
  pontos: number | null;
  aplicavel?: boolean;
  detalhe: string;
  /** Texto curto de "como melhorar" — usado na página /minha-performance. */
  sugestao: string;
}

export interface NotaBkoResultado {
  bko_id: string;
  bko_nome: string;
  bko_funcao: string;
  tipo: TipoBko;
  competencia: string;
  nota: number;
  faixa: FaixaNotaBko;
  diagnostico: string;
  breakdown: PilarNotaBko[];
  competencia_fechada?: boolean;
  fechado_em?: string | null;
  periodo_inicio?: string;
  periodo_fim?: string;
  provisoria?: boolean;
  pagamento_liberado?: boolean;
  competencia_status?: string;
  avaliacoes_consideradas?: number;
  cobertura_atendimento?: {
    conversas_com_acao: number;
    mensagens_publicas: number;
    conversas_avaliadas: number;
    concluidas_sem_avaliacao: number;
    conversas_em_aberto: number;
    conversas_sem_caso: number;
    conversas_com_caso_anterior?: number;
    conversas_com_resposta_cliente: number;
  };
  nota_aplicavel?: boolean;
  percentual_produtividade?: number;
  regime_vigencia_origem?: string | null;
}

export async function consultarNotaBkoMes(bkoId: string, tipo: TipoBko, competenciaStr: string): Promise<NotaBkoResultado> {
  const { data, error } = await chamarRpc('calcular_nota_bko_mes', {
    p_bko_id: bkoId,
    p_competencia: competenciaStr,
    p_tipo: tipo,
  });
  if (error) throw error;
  const resultado = data as NotaBkoResultado | null;
  if (resultado?.nota == null || !Number.isFinite(Number(resultado.nota))) throw new Error('O servidor não retornou uma nota mensal válida.');
  return { ...resultado, nota: Number(resultado.nota) };
}

/**
 * Calcula a nota mensal do BKO (0..100) via RPC `calcular_nota_bko_mes`.
 * Pilares próprios por cargo (CONTRATO/TRAMITACAO/CREDITO/PORTABILIDADE/ANALISTA),
 * cada um com peso, pontos atingidos, detalhe atual e sugestão de melhoria.
 *
 * - `bkoId`: usuário BKO
 * - `tipo`: tipo da função do BKO (define quais pilares são avaliados)
 * - `competencia`: data dentro do mês (default mês corrente)
 */
export function useNotaBkoMes(
  bkoId?: string | null,
  tipo?: TipoBko | null,
  competencia?: Date,
) {
  const competenciaStr = format(startOfMonth(competencia ?? new Date()), 'yyyy-MM-dd');

  return useQuery<NotaBkoResultado | null>({
    queryKey: ['nota-bko-mes', bkoId, tipo, competenciaStr],
    enabled: !!bkoId && !!tipo,
    staleTime: 5 * 60 * 1000,
    refetchInterval: tipo === 'ANALISTA' ? 60_000 : 5 * 60 * 1000,
    refetchOnWindowFocus: true,
    queryFn: async () => {
      if (!bkoId || !tipo) return null;
      return consultarNotaBkoMes(bkoId, tipo, competenciaStr);
    },
  });
}

export const FAIXA_BKO_LABEL: Record<FaixaNotaBko, string> = {
  verde_forte: 'Excelente',
  verde: 'Bom',
  amarelo: 'Atenção',
  laranja: 'Baixo',
  vermelho: 'Crítico',
};

/** Cor de fundo do badge da nota — paleta da marca (bordô + tokens semânticos suaves). */
export const FAIXA_BKO_BG: Record<FaixaNotaBko, string> = {
  verde_forte: 'bg-success/15 text-success border-success/30',
  verde: 'bg-success/10 text-success border-success/25',
  amarelo: 'bg-warning/15 text-warning border-warning/30',
  laranja: 'bg-primary/15 text-primary border-primary/30',
  vermelho: 'bg-primary/25 text-primary border-primary/40',
};

/** Cor sólida da barra de progresso do pilar. */
export const FAIXA_BKO_BARRA: Record<FaixaNotaBko, string> = {
  verde_forte: 'bg-success',
  verde: 'bg-success/80',
  amarelo: 'bg-warning',
  laranja: 'bg-primary/60',
  vermelho: 'bg-primary',
};

/**
 * Infere o tipo de BKO a partir da função do usuário.
 * BKO_COMPLETO mapeia pra CONTRATO por default (área principal do completo).
 *
 * ⚠️ BKO_INPUT usa a régua TRAMITACAO, não a de CONTRATO (Douglas 20/08/2026).
 * INPUT e TRAMITAÇÃO são a MESMA área no CRM — `QUADROS_POR_FUNCAO` em
 * `useFilasBKO` manda as duas para TRAMITACAO_VIVO + TRAMITACAO_PRIME. Antes,
 * por cair no `return 'CONTRATO'` do final, a Annye era avaliada por criação,
 * aceite e SLA de contrato, trabalho que ela não faz: dava nota 50 (= comissão
 * ZERO) contra 68 na régua da área dela.
 */
export { inferirTipoBko } from '../../../backend/src/domain/comissao/tipoBko';
